import { useState } from 'react'
import type { BootstrapRefusal } from '@domain/ports/auth-gateway'
import { Glass } from '@ui/glass/Glass'
import { useAuth } from './auth-context'
import type { Session } from './session-state'
import { Botao } from '@ui/glass/Botao'

/** Os estados em que não há nem app nem tela de login para mostrar. */
type PendingSession = Extract<Session, { status: 'loading' | 'preparing' | 'unprepared' }>

/**
 * Espera anunciada.
 *
 * `quiet` deixa a espera **invisível**, só para leitor de tela. É o que o
 * estado `loading` usa: ele dura um quadro para quem já tem sessão, e qualquer
 * pixel desenhado ali seria a piscada que a definição de pronto da fase
 * proíbe. `preparing` é o contrário — a pessoa acabou de clicar em entrar (ou
 * de abrir o app) e merece saber que algo está acontecendo.
 */
export function Waiting({ label, quiet = false }: { label: string; quiet?: boolean }) {
  if (quiet) {
    return (
      <p role="status" className="sr-only">
        {label}
      </p>
    )
  }
  return (
    <section className="mx-auto flex min-h-dvh max-w-md items-center justify-center px-4">
      <p role="status" className="text-sm text-[var(--text-muted)]">
        {label}
      </p>
    </section>
  )
}

/**
 * O bootstrap recusou. As duas recusas não são a mesma coisa e não podem
 * parecer a mesma coisa: `already-linked` (decisão 48/57) **nunca** se conserta
 * retentando, e oferecer um botão de "tentar de novo" ali seria mentir sobre o
 * que o app pode fazer. `retryable` se conserta — inclusive sozinha, na próxima
 * abertura, porque o bootstrap roda em toda abertura (decisão 42).
 */
function HouseholdUnavailable({ refusal }: { refusal: BootstrapRefusal }) {
  const { retryBootstrap, signOut } = useAuth()
  const [signOutFailed, setSignOutFailed] = useState(false)

  async function handleSignOut() {
    setSignOutFailed(false)
    try {
      await signOut()
    } catch {
      setSignOutFailed(true)
    }
  }

  return (
    <section className="mx-auto flex min-h-dvh max-w-md items-center justify-center px-4">
      <Glass level="frost" className="w-full p-6">
        <h1 className="mb-2 text-lg font-semibold tracking-tight">Não foi possível abrir a casa</h1>
        <p role="alert" className="mb-4 text-sm text-[var(--text-muted)]">
          {refusal === 'already-linked'
            ? 'Este e-mail já está vinculado a outra conta nesta casa. Tentar de novo não resolve: é preciso desfazer o vínculo antigo.'
            : 'Não conseguimos preparar a casa agora. Isso costuma se resolver sozinho na próxima abertura do app.'}
        </p>
        <div className="flex gap-2">
          {refusal === 'retryable' && (
            <Botao tom="primario" type="button" onClick={retryBootstrap}>
              Tentar de novo
            </Botao>
          )}
          <button
            type="button"
            onClick={() => void handleSignOut()}
            className="rounded-[var(--radius-md)] px-4 py-2 text-sm font-medium text-[var(--text-muted)]"
          >
            Sair
          </button>
        </div>
        {signOutFailed && (
          <p role="alert" className="mt-3 text-sm text-[var(--text-muted)]">
            Não foi possível sair agora. Tente de novo.
          </p>
        )}
      </Glass>
    </section>
  )
}

/** A tela de cada estado intermediário. Quem chama já separou `signedOut` e
 * `ready`, que são os dois estados em que existe algo de verdade a mostrar. */
export function Interstitial({ session }: { session: PendingSession }) {
  switch (session.status) {
    case 'loading':
      return <Waiting label="Carregando" quiet />
    case 'preparing':
      return <Waiting label="Preparando sua casa…" />
    case 'unprepared':
      return <HouseholdUnavailable refusal={session.refusal} />
  }
}
